// Import dependencies
import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import '../styles/CelendarStyles.css'; // Import the custom styles
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getJobsiteAttendance_employerView, reset } from '../features/workHistory/workHistroySlice';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AttendanceByJobsite = () => {
    // Redux and router hooks
    const dispatch = useDispatch();
    const location = useLocation();
    const { jobsiteID, jobsiteName } = location.state;

    // State for date range selection
    const [dateRange, setDateRange] = useState([new Date(), new Date()]);
    const [dateFrom, dateTo] = dateRange;

    // Redux state selectors
    const { employerView_byJobsite: attendanceData, isError, message } = useSelector(state => state.workHistory);

    // Handle calendar date range changes
    const onChange = (range) => {
        if (range.length === 2) {
            const [start, end] = range;
            const diffTime = Math.abs(end - start);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            // Limit the date range to a maximum of 7 days
            if (diffDays > 6) {
                const newEnd = new Date(start);
                newEnd.setDate(start.getDate() + 6);
                setDateRange([start, newEnd]);
            } else {
                setDateRange(range);
            }
        } else {
            setDateRange(range);
        }
    };

    // Fetch attendance data when date range changes
    useEffect(() => {
        const calendarParams = {
            jobsiteID,
            dateFrom: dateFrom.toISOString(),
            dateTo: dateTo.toISOString(),
        };
        dispatch(getJobsiteAttendance_employerView(calendarParams));
    }, [dateRange, jobsiteID, dispatch]);

    // Handle errors and display toast notifications
    useEffect(() => {
        if (isError) {
            toast.error(message, {
                position: "top-right",
                autoClose: 2000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
            dispatch(reset());
        }
    }, [isError, message, dispatch]);

    // Group attendance data by date
    const groupByDate = (data) => {
        return data.reduce((acc, item) => {
            const date = new Date(item.localDate).toLocaleDateString('en-US', { timeZone: 'UTC' }); // Format in UTC
            if (!acc[date]) {
                acc[date] = [];
            }
            acc[date].push(item);
            return acc;
        }, {});
    };

    const groupedData = groupByDate(attendanceData);

    // Export data to CSV
    const exportToCSV = () => {
        if (!attendanceData.length) {
            toast.warn('No data to export');
            return;
        }

        const headers = ['Jobsite', 'Date', 'Name', 'Role', 'Phone', 'Time In', 'Time Out', 'Hours Worked'];
        const csvData = attendanceData.map(worker => {
            const timeIn = new Date(worker.timeIn);
            const timeOut = worker.timeOut ? new Date(worker.timeOut) : null;
            const hoursWorked = timeOut ? ((timeOut - timeIn) / (1000 * 60 * 60)).toFixed(2) : 'N/A';
            
            return [
                jobsiteName,
                new Date(worker.localDate).toLocaleDateString('en-US', { timeZone: 'UTC' }),
                `${worker.name} ${worker.lastName}`,
                worker.role,
                worker.phone,
                timeIn.toLocaleTimeString(),
                timeOut ? timeOut.toLocaleTimeString() : 'N/A',
                hoursWorked
            ];
        });

        const csvContent = [headers, ...csvData]
            .map(row => row.map(field => `"${field}"`).join(','))
            .join('\n');

        // Add UTF-8 BOM for proper Excel encoding
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${jobsiteName}_attendance_${dateFrom.toISOString().split('T')[0]}_to_${dateTo.toISOString().split('T')[0]}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        
        toast.success('Data exported successfully!');
    };

    return (
        <div className="flex flex-col md:flex-row">
            {/* Calendar Section */}
            <div className="w-full md:w-1/3 p-4 min-w-[300px]">
                <div className="calendar-wrapper">
                    <Calendar
                        selectRange={true}
                        onChange={onChange}
                        value={dateRange}
                    />
                    <div className="date-info">
                        <p className="mt-4">Date From: {dateFrom.toDateString()}</p>
                        <p>Date To: {dateTo.toDateString()}</p>
                    </div>
                </div>
            </div>

            {/* Attendance Records Section */}
            <div className="w-full md:w-2/3 p-4">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-kanit p-2 bg-teal text-ivory rounded-lg shadow-md">
                        Attendance at {jobsiteName}
                    </h1>
                    <button
                        onClick={exportToCSV}
                        className="bg-orangem hover:bg-orange-600 text-white font-kanit px-6 py-3 rounded-lg shadow-md transition-colors duration-200 flex items-center gap-2"
                        disabled={!attendanceData.length}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Export Data
                    </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 overflow-y-scroll max-h-[700px]"> {/* Increased max height */}
                    {Object.keys(groupedData)
                        .sort((a, b) => new Date(a) - new Date(b))
                        .map(date => (
                            <div key={date} className="col-span-full">
                                <h2 className="text-xl font-kanit text-orangem mb-4">{date}</h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {groupedData[date].map((worker, index) => (
                                        <div key={index} className="bg-ivory rounded-lg shadow-lg p-4 flex flex-col items-center">
                                            <img
                                                src={worker.profilePicture}
                                                alt={`${worker.name} ${worker.lastName}`}
                                                className="w-24 h-24 rounded-full mb-4 border-4 border-teal"
                                            />
                                            <p className="text-lg font-kanit text-charcoal font-bold mb-2">
                                                {worker.name} {worker.lastName}
                                            </p>
                                            <p className="text-sm font-sans text-gray-700">Role: {worker.role}</p>
                                            <p className="text-sm font-sans text-gray-700">Phone: {worker.phone}</p>
                                            <p className="text-sm font-sans text-gray-700">Time In: {new Date(worker.timeIn).toLocaleTimeString()}</p>
                                            {worker.timeOut && (
                                                <p className="text-sm font-sans text-gray-700">Time Out: {new Date(worker.timeOut).toLocaleTimeString()}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                </div>
            </div>
        </div>
    );
};

export default AttendanceByJobsite;